export const ACTIVE_SUPPORT_STATUSES = ['open', 'in_progress', 'waiting_customer'];
export const SUPPORT_STATUS_OPTIONS = [
  'open',
  'in_progress',
  'waiting_customer',
  'resolved',
  'closed',
];
export const SUPPORT_PRIORITY_OPTIONS = ['low', 'normal', 'high', 'critical'];
export const SUPPORT_CHANNEL_OPTIONS = ['portal', 'email', 'phone', 'internal', 'automation'];
export const NOTIFICATION_LEVEL_OPTIONS = ['info', 'success', 'warning', 'critical'];

function normalizeToken(value) {
  return String(value || '').trim().toLowerCase();
}

export function parseDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatCaseStatus(value) {
  const token = normalizeToken(value);
  if (!token) return 'Unknown';
  return token
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatCasePriority(value) {
  const token = normalizeToken(value);
  if (!token) return 'Unknown';
  return token.charAt(0).toUpperCase() + token.slice(1);
}

export function formatCaseChannel(value) {
  const token = normalizeToken(value);
  if (!token) return 'Unknown';
  return token.charAt(0).toUpperCase() + token.slice(1);
}

export function formatNotificationLevel(value) {
  const token = normalizeToken(value);
  if (!token) return 'Info';
  return token.charAt(0).toUpperCase() + token.slice(1);
}

export function isActiveSupportCase(record) {
  return ACTIVE_SUPPORT_STATUSES.includes(normalizeToken(record?.status));
}

export function isCriticalSupportCase(record) {
  return normalizeToken(record?.priority) === 'critical';
}

export function isStaleSupportCase(record, thresholdHours = 48) {
  if (!isActiveSupportCase(record)) return false;
  const reference =
    parseDate(record?.lastActivityAt) ||
    parseDate(record?.updatedAt) ||
    parseDate(record?.openedAt) ||
    parseDate(record?.createdAt);
  if (!reference) return false;
  return reference.getTime() < Date.now() - thresholdHours * 3600 * 1000;
}

export function buildSupportTrendRows(records = [], days = 7) {
  const now = new Date();
  return Array.from({ length: days }, (_, index) => {
    const dayStart = new Date(now);
    dayStart.setHours(0, 0, 0, 0);
    dayStart.setDate(now.getDate() - (days - 1 - index));
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayStart.getDate() + 1);

    const value = records.filter((record) => {
      const openedAt = parseDate(record?.openedAt) || parseDate(record?.createdAt);
      return openedAt ? openedAt >= dayStart && openedAt < dayEnd : false;
    }).length;

    return {
      label: dayStart.toLocaleDateString(undefined, { weekday: 'short' }),
      value,
    };
  });
}

export function buildSupportStatusRows(records = []) {
  return SUPPORT_STATUS_OPTIONS.map((status) => ({
    label: formatCaseStatus(status),
    value: records.filter((record) => normalizeToken(record?.status) === status).length,
  })).filter((row) => row.value > 0);
}

export function buildSupportPriorityRows(records = []) {
  return SUPPORT_PRIORITY_OPTIONS.map((priority) => ({
    label: formatCasePriority(priority),
    value: records.filter((record) => normalizeToken(record?.priority) === priority).length,
  })).filter((row) => row.value > 0);
}

export function buildSupportChannelRows(records = []) {
  const baseRows = SUPPORT_CHANNEL_OPTIONS.map((channel) => ({
    label: formatCaseChannel(channel),
    value: records.filter((record) => normalizeToken(record?.channel) === channel).length,
  }));

  const extraChannels = Array.from(
    records.reduce((map, record) => {
      const token = normalizeToken(record?.channel);
      if (!token || SUPPORT_CHANNEL_OPTIONS.includes(token)) return map;
      map.set(token, (map.get(token) || 0) + 1);
      return map;
    }, new Map())
  ).map(([channel, value]) => ({
    label: formatCaseChannel(channel),
    value,
  }));

  return [...baseRows, ...extraChannels].filter((row) => row.value > 0);
}

export function buildNotificationLevelRows(records = []) {
  return NOTIFICATION_LEVEL_OPTIONS.map((level) => ({
    label: formatNotificationLevel(level),
    value: records.filter((record) => normalizeToken(record?.level) === level).length,
  })).filter((row) => row.value > 0);
}

export function buildNotificationTrendRows(records = [], days = 7) {
  const now = new Date();
  return Array.from({ length: days }, (_, index) => {
    const dayStart = new Date(now);
    dayStart.setHours(0, 0, 0, 0);
    dayStart.setDate(now.getDate() - (days - 1 - index));
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayStart.getDate() + 1);

    const value = records.filter((record) => {
      const createdAt = parseDate(record?.createdAt);
      return createdAt ? createdAt >= dayStart && createdAt < dayEnd : false;
    }).length;

    return {
      label: dayStart.toLocaleDateString(undefined, { weekday: 'short' }),
      value,
    };
  });
}

export function readCaseReviewWorkspace(record) {
  const workspace = record?.metadata?.reviewWorkspace;
  return workspace && typeof workspace === 'object' && !Array.isArray(workspace)
    ? workspace
    : {};
}

export function getSupportCodeState(record) {
  const expiresAt = parseDate(record?.supportCodeExpiresAt);
  if (expiresAt) {
    return expiresAt.getTime() < Date.now() ? 'expired' : 'active';
  }
  if (record?.supportCodeHash) return 'issued';
  return 'not_issued';
}

export function formatSupportCodeState(value) {
  const token = normalizeToken(value);
  if (!token) return 'Not issued';
  if (token === 'not_issued') return 'Not issued';
  return token
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function buildAdminWorkspaceSummary({
  supportCases = [],
  commandAlerts = [],
  stats = {},
  notifications = [],
} = {}) {
  const activeCases = supportCases.filter(isActiveSupportCase);
  const criticalCases = activeCases.filter(isCriticalSupportCase);
  const waitingCases = activeCases.filter(
    (record) => normalizeToken(record?.status) === 'waiting_customer'
  );
  const staleCases = activeCases.filter((record) => isStaleSupportCase(record));
  const openAlerts = commandAlerts.filter(
    (alert) => normalizeToken(alert?.status) === 'open'
  );
  const criticalAlerts = openAlerts.filter(
    (alert) => normalizeToken(alert?.severity) === 'critical'
  );
  const warningSystems = Number(stats?.warningSystems || 0);
  const recentNotifications = notifications.filter(Boolean);
  const escalatedNotifications = recentNotifications.filter((item) =>
    ['warning', 'critical'].includes(normalizeToken(item?.level))
  );

  let severity = 'healthy';
  let title = 'Operations look stable right now';
  let description =
    'The support queue, command alerts, and outbound customer notifications are calm.';

  if (criticalCases.length || criticalAlerts.length) {
    severity = 'critical';
    title = 'Immediate attention is needed';
    description = `${criticalCases.length} critical support case${
      criticalCases.length === 1 ? '' : 's'
    } and ${criticalAlerts.length} critical command alert${
      criticalAlerts.length === 1 ? '' : 's'
    } are active right now.`;
  } else if (
    staleCases.length ||
    waitingCases.length ||
    warningSystems ||
    openAlerts.length ||
    escalatedNotifications.length
  ) {
    severity = 'warning';
    title = 'A few queues need review';
    description = `${staleCases.length} stale case${
      staleCases.length === 1 ? '' : 's'
    }, ${waitingCases.length} waiting-on-customer case${
      waitingCases.length === 1 ? '' : 's'
    }, and ${openAlerts.length} live command alert${
      openAlerts.length === 1 ? '' : 's'
    } are still open.`;
  }

  const alerts = [];

  if (criticalCases.length || criticalAlerts.length) {
    alerts.push({
      id: 'critical-work',
      severity: 'critical',
      title: 'Immediate attention',
      description:
        'Critical support work or command alerts are active and should be reviewed first.',
      count: criticalCases.length + criticalAlerts.length,
      to: criticalCases.length ? '/support?priority=critical' : '/command-alerts',
    });
  }

  if (staleCases.length || waitingCases.length) {
    alerts.push({
      id: 'support-drift',
      severity: 'warning',
      title: 'Support queue drift',
      description:
        'Some active support cases are stale or waiting on a customer follow-up.',
      count: staleCases.length + waitingCases.length,
      to: staleCases.length ? '/support?view=stale' : '/support?view=waiting',
    });
  }

  if (warningSystems) {
    alerts.push({
      id: 'systems-warning',
      severity: 'warning',
      title: 'Systems need review',
      description:
        'One or more managed systems are in a warning state and may need operational follow-up.',
      count: warningSystems,
      to: '/systems',
    });
  }

  if (openAlerts.length) {
    alerts.push({
      id: 'command-alerts',
      severity: criticalAlerts.length ? 'warning' : 'info',
      title: 'Command alerts',
      description:
        'Cross-product command alerts are open and may need acknowledgement or resolution.',
      count: openAlerts.length,
      to: '/command-alerts',
    });
  }

  if (recentNotifications.length) {
    alerts.push({
      id: 'user-notifications',
      severity: escalatedNotifications.length ? 'info' : 'healthy',
      title: 'Notification center',
      description:
        'Recent customer-facing notifications are available for review and follow-up.',
      count: recentNotifications.length,
      to: escalatedNotifications.length ? '/notifications?view=attention' : '/notifications',
    });
  }

  if (!alerts.length) {
    alerts.push({
      id: 'all-clear',
      severity: 'healthy',
      title: 'All clear',
      description:
        'No urgent support, alerting, or notification pressure is showing right now.',
      count: 0,
      to: '/dashboard',
    });
  }

  return {
    severity,
    title,
    description,
    alerts,
    activeCases,
    criticalCases,
    waitingCases,
    staleCases,
    openAlerts,
    criticalAlerts,
    warningSystems,
    recentNotifications,
    escalatedNotifications,
  };
}

export function buildCaseReviewWarnings(record) {
  if (!record) return [];

  const warnings = [];
  const reviewWorkspace = readCaseReviewWorkspace(record);
  const supportCodeState = getSupportCodeState(record);

  if (!record?.assignedAdminId) {
    warnings.push({
      id: 'unassigned',
      severity: 'critical',
      title: 'No owner assigned',
      description: 'This case still has no assigned admin, so follow-up could stall.',
    });
  }

  if (isCriticalSupportCase(record) && isActiveSupportCase(record)) {
    warnings.push({
      id: 'critical-priority',
      severity: 'warning',
      title: 'Critical customer impact',
      description: 'This case is marked critical and should be reviewed before lower-priority queue work.',
    });
  }

  if (isStaleSupportCase(record)) {
    warnings.push({
      id: 'stale-case',
      severity: 'warning',
      title: 'Case looks stale',
      description: 'There has not been recent activity on this open case, so it may need an update or customer response.',
    });
  }

  if (normalizeToken(record?.status) === 'waiting_customer') {
    warnings.push({
      id: 'waiting-customer',
      severity: 'info',
      title: 'Waiting on customer',
      description: 'The queue says the next move depends on the customer, so make sure the last outreach is still current.',
    });
  }

  if (record?.accessAssistanceRequested) {
    warnings.push({
      id: 'access-assistance',
      severity: 'info',
      title: 'Access assistance requested',
      description: 'The customer asked for support access help, so double-check any portal or OTP guidance before closing the case.',
    });
  }

  if (
    record?.accessAssistanceRequested &&
    (supportCodeState === 'not_issued' || supportCodeState === 'expired')
  ) {
    warnings.push({
      id: 'support-code-gap',
      severity: 'warning',
      title: 'Access help may be blocked',
      description: 'This case requests login or OTP help, but the support access window is missing or already expired.',
    });
  }

  if (!String(record?.summary || '').trim() && !String(record?.internalNotes || '').trim()) {
    warnings.push({
      id: 'thin-context',
      severity: 'warning',
      title: 'Thin case context',
      description: 'There is very little written context on this case yet, which makes handoff and review harder.',
    });
  }

  if (isActiveSupportCase(record) && !String(reviewWorkspace.nextAction || '').trim()) {
    warnings.push({
      id: 'next-action-missing',
      severity: 'warning',
      title: 'Next action is missing',
      description: 'The review workspace does not say what should happen next, which makes ownership and follow-up less clear.',
    });
  }

  if (
    !isActiveSupportCase(record) &&
    !String(reviewWorkspace.resolutionSummary || '').trim()
  ) {
    warnings.push({
      id: 'resolution-summary-missing',
      severity: 'info',
      title: 'Resolution summary is empty',
      description: 'The case looks closed or resolved, but there is no short explanation of what fixed it or why it was closed.',
    });
  }

  if (
    ['email', 'phone'].includes(normalizeToken(record?.channel)) &&
    !String(record?.requestedBy?.email || record?.requestedBy?.phone || '').trim()
  ) {
    warnings.push({
      id: 'contact-detail-missing',
      severity: 'info',
      title: 'Follow-up contact detail is missing',
      description: 'This case came in through a direct support channel, but there is no clear email or phone contact attached to it.',
    });
  }

  return warnings;
}
